import { USER_TYPES } from 'API';
import { generateRandomNumber } from 'components';
import {
  createUserProfile,
  getUserProfile,
  UseCreateBrandProfile,
  UseGetBrandProfileByUserEmail,
  UseUpdateUserProfile,
  useUserTypeSetter,
} from 'hooks';
import withApolloProvider from 'hooks/apollo/withApollo';
import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from 'state/auth';
import { AuthContextType } from 'state/types/authTypes';
import { initialProfileState, ProfileProps } from 'utils';
import { removeEmpty } from 'utils/utils';
import { ProfileContext } from './profile.context';
import { IUpdateBrandProfile } from './profile.interface';

interface HocProps {
  shouldCallApi?: boolean;
}

export function withProfile<T>(
  Component: React.FC<T & ProfileProps & HocProps>
): React.FC<T & HocProps> {
  return withApolloProvider((props: T & HocProps) => {
    const { shouldCallApi } = props;
    const { profileState, setProfileState } = useContext(ProfileContext);
    const { authState, setAuthState } =
      useContext<AuthContextType>(AuthContext);
    const { getProfile, profileData, isProfileExists, error, loading } =
      getUserProfile();
    const { createBrand } = UseCreateBrandProfile();
    const { getBrand } = UseGetBrandProfileByUserEmail();

    const {
      createProfile,
      profileData: createProfileData,
      loading: createProfileLoading,
    } = createUserProfile();
    const {
      updateProfile,
      profileData: updateProfileData,
      loading: updateProfileLoading,
    } = UseUpdateUserProfile();
    const {
      setUserType,
      data: userTypeData,
      loading: userTypeLoading,
    } = useUserTypeSetter();

    const { isLoggedIn, email, userId, name } = authState;
    const [createProfileApiCall, updateCreateProfileApiCall] = useState(false);
    const [isApiCalled, setIsApiCalled] = useState(false);

    const refetchProfile = (force?: boolean): void => {
      if (email && (shouldCallApi || force)) {
        setIsApiCalled(true);
        setProfileState({ isLoading: true });
        getProfile({ variables: { id: userId } });
      }
    };

    // FIXME: ignore policy
    const editBrandProfile = async (
      input: IUpdateBrandProfile
    ): Promise<void> => {
      if (profileState?.data?.id) {
        const { description, tiktokHandler, id } = profileState.data;
        const prevInput = { id, description, tiktokHandler };
        updateProfile({
          variables: { input: { ...prevInput, ...input } },
          errorPolicy: 'ignore',
        });
      }
    };

    const editCreatorProfile = (input: IUpdateBrandProfile): void => {
      if (!profileState?.data?.id) {
        return;
      }

      updateProfile({
        variables: {
          input: { id: profileState.data.id, ...input },
          errorPolicy: 'ignore',
          ignoreResults: true,
        },
      });

      setProfileState((prev) => {
        if (!prev.data) return { isLoading: false };

        return {
          ...prev,
          data: {
            ...prev.data,
            country: input?.country,
            termsAndConditions: input?.termsAndConditions,
          },
        };
      });
    };

    const cleanProfileState = (): void => {
      setProfileState({ isLoading: false });
    };

    useEffect(() => {
      if (
        email &&
        isLoggedIn &&
        userId &&
        !profileState.data &&
        shouldCallApi
      ) {
        refetchProfile();
      }
    }, [email, isLoggedIn, userId, shouldCallApi]);

    useEffect(() => {
      if (profileState?.data?.id) {
        if (
          profileState.data.lastLoginDate &&
          new Date().getTime() -
          new Date(profileState.data.lastLoginDate).getTime() >
          100000
        ) {
          updateProfile({
            variables: {
              input: { lastLoginDate: new Date(), id: profileState?.data?.id },
            },
          });
        }
      }
    }, []);

    useEffect(() => {
      if (!loading && shouldCallApi && isApiCalled) {
        setIsApiCalled(false);
        if (profileData && isProfileExists) {
          setProfileState({
            data: profileData,
            isLoading: false,
            error: undefined,
          });
          return;
        }

        if (!isProfileExists && !createProfileApiCall) {
          const input = {
            name: name,
            email: email,
            id: userId,
            uniqueId: generateRandomNumber(6),
          };

          createProfile({
            variables: {
              input,
            },
            errorPolicy: 'ignore',
          });
          updateCreateProfileApiCall(true);
          setAuthState({ ...authState, name });
          setProfileState({ isLoading: false, error, data: null });
        }
      }
    }, [profileData, loading, error]);

    useEffect(() => {
      (async () => {
        if (
          !profileState.data ||
          profileState.data.userType !== USER_TYPES.BRAND_USER
        ) {
          return;
        }

        const data = await getBrand({
          variables: { userEmail: profileState.data?.email || '' },
        });

        const brandProfile = data.data?.brandProfilesByUserEmail?.items?.[0];
        if (brandProfile != null) {
          return;
        }

        createBrand({
          variables: {
            input: {
              id: profileState.data.id,
              name: profileState.data.name,
              userEmail: profileState.data.email,
              userProfileBrandId: profileState.data.id,
            },
            condition: {
              userEmail: { attributeExists: false },
              userProfileBrandId: { attributeExists: false },
            },
          },
          errorPolicy: 'ignore',
        });
      })();
    }, [createBrand, profileState.data, getBrand]);

    useEffect(() => {
      if (createProfileData && !createProfileLoading)
        setUserType({
          variables: {
            profileId: createProfileData.id,
            userType:
              localStorage.getItem('userType') || USER_TYPES.CREATIVE_USER,
          },
        });
    }, [createProfileLoading, createProfileData]);

    useEffect(() => {
      if (userTypeData && !userTypeLoading) {
        localStorage.removeItem('userType');
        window.location.reload();
      }
    }, [userTypeData, userTypeLoading]);

    useEffect(() => {
      if (updateProfileData && !updateProfileLoading) {
        const filteredProfileData = { ...updateProfileData };
        delete filteredProfileData.userType;
        setProfileState((prev) => {
          return {
            data: { ...prev.data, ...removeEmpty(filteredProfileData) },
            isLoading: false,
            error: undefined,
          };
        });
      }
    }, [updateProfileLoading, updateProfileData]);

    useEffect(() => {
      if (!isLoggedIn) setProfileState(initialProfileState);
    }, [isLoggedIn]);

    const profileProps: ProfileProps = {
      profileState: { ...profileState },
      setProfileState,
      refetchProfile,
      cleanProfileState,
      editBrandProfile,
      editCreatorProfile,
      updateProfileData,
      updateProfileLoading,
    };

    return <Component {...props} {...profileProps} />;
  });
}
export default withProfile;
